import type {DoppeViewerHeadlessAuthCallbackMessage} from '../../../client-server-common';
import {
    DoppeHeadlessLoginMode,
    DoppeViewerHeadlessAuthMessageType,
    DoppeViewerHeadlessAuthStartPath,
    DoppeViewerHeadlessTokensCookieName,
} from '../../../client-server-common';
import {DoppeViewerAppClientServiceBase} from '../doppe-viewer-app-client-service-base';
import {assertNotNullable, asyncExecuteInBackground, EventEmitter} from "@wix/devzai-utils-common";
import {browserOpenNewTab} from "@wix/devzai-utils-browser";

/**
 * Browser-side client for Wix Headless SDK integration.
 * This service orchestrates navigation to the dedicated auth pages
 * and reads/writes headless tokens from cookies.
 * The actual @wix/sdk interaction happens in the headless-auth entry point.
 */
export class DoppeViewerHeadlessClientService extends DoppeViewerAppClientServiceBase {

    private clientId: string | null = null;

    private eventEmitter = new EventEmitter<{
        loginCompleted: void;
        loggedOut: void;
    }>();

    /** Emitted when a login flow completes (redirect or popup). */
    public get eventLoginCompleted() {
        return this.eventEmitter.createEventReference('loginCompleted');
    }

    /** Emitted when the user logs out. */
    public get eventLoggedOut() {
        return this.eventEmitter.createEventReference('loggedOut');
    }

    /**
     * Initializes the headless service with the OAuth client ID.
     * Must be called after the cookie service is initialized.
     * Detects if the user just completed a redirect login and emits loginCompleted.
     */
    public initialize(
        options: {
            clientId: string;
        }
    ) {
        this.clientId = options.clientId;

        // Detect redirect login completion (auth_complete param set by the callback page)
        const url = new URL(window.location.href);

        if (url.searchParams.has('auth_complete')) {
            url.searchParams.delete('auth_complete');
            window.history.replaceState({}, '', url.toString());
            this.eventEmitter.emit('loginCompleted');
        }
    }

    /** Returns the client ID or null if headless is not configured for this site. */
    public getClientIdOrNull(): string | null {
        return this.clientId;
    }

    public assertClientId() {
        return assertNotNullable(this.clientId, 'DoppeViewerHeadlessClientService not initialized with clientId');
    }

    /**
     * Starts the login flow by navigating to the dedicated /__auth_start page.
     * For popup mode: opens a new window and internally waits for the result.
     * For redirect mode: navigates the current tab away.
     * Emits loginCompleted when the popup flow completes successfully.
     */
    public startLogin(
        mode: DoppeHeadlessLoginMode
    ): void {

        if (!this.clientId) {
            return;
        }

        const originalUri = window.location.href;
        const cookiePath = doppeViewerHeadlessClientServiceResolveCookiePath();

        const startUrl = new URL(DoppeViewerHeadlessAuthStartPath, window.location.origin);
        startUrl.searchParams.set('clientId', this.clientId);
        startUrl.searchParams.set('loginMode', mode);
        startUrl.searchParams.set('originalUri', originalUri);
        startUrl.searchParams.set('cookiePath', cookiePath);

        switch (mode) {
            case DoppeHeadlessLoginMode.Redirect: {
                window.location.href = startUrl.toString();
                return;
            }
            case DoppeHeadlessLoginMode.Popup: {
                browserOpenNewTab(startUrl.toString());
                asyncExecuteInBackground(async () => {
                    await new Promise<void>((resolve, reject) => {
                        const messageHandler = (event: MessageEvent) => {
                            const data = event.data as DoppeViewerHeadlessAuthCallbackMessage | undefined;

                            if (data?.type !== DoppeViewerHeadlessAuthMessageType) {
                                return;
                            }

                            window.removeEventListener('message', messageHandler);

                            if (data.error) {
                                reject(new Error(data.error));
                            } else {
                                resolve();
                            }
                        };

                        window.addEventListener('message', messageHandler);
                    });

                    this.eventEmitter.emit('loginCompleted');
                })
                return;
            }
        }
    }

    /** Returns whether the user is currently logged in by checking the headless tokens cookie. */
    public isLoggedIn(): boolean {
        const cookieAccessor = this.clientServices.doppeViewerAppCookieService.consentSafeCookieStorageAccessor;

        if (!cookieAccessor) {
            return false;
        }

        const tokens = cookieAccessor.getItem<{refreshToken?: {role?: string}}>(DoppeViewerHeadlessTokensCookieName);

        return tokens?.refreshToken?.role === 'member';
    }

    /** Clears the headless tokens cookie and emits loggedOut. */
    public logout(): void {
        const cookieAccessor = this.clientServices.doppeViewerAppCookieService.consentSafeCookieStorageAccessor;
        cookieAccessor?.removeItem(DoppeViewerHeadlessTokensCookieName);
        this.eventEmitter.emit('loggedOut');
    }
}

/** Resolves the cookie path based on the current hostname (path-scoped for shared domains). */
function doppeViewerHeadlessClientServiceResolveCookiePath(): string {
    const hostname = window.location.hostname;
    const sharedDomains = ['hopp.bio', 'bymo.io', 'doppe-viewer-sec.com', 'doppe.com', 'hopp.to'];

    for (const domain of sharedDomains) {
        if (hostname === domain || hostname === `www.${domain}`) {
            // On shared domains, the cookie path is /<subdomain> (first path segment)
            const firstSegment = window.location.pathname.split('/')[1];

            if (firstSegment) {
                return `/${firstSegment}`;
            }
        }
    }

    return '/';
}
